// frontend/src/components/current-load/CurrentLoadRoutePanel.jsx

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import {
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Info,
  Pencil,
  Plus,
  X,
} from "lucide-react";

const DAY_KEYS = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
];

const LOADING_ACCESS_I18N_KEYS = {
  SIDE: "side",
  REAR: "rear",
  TOP: "top",
  DOCK: "dock",
  ANY: "any",
};

const HANDLING_EQUIPMENT_I18N_KEYS = {
  FORKLIFT: "forklift",
  PALLET_JACK: "palletJack",
  ELECTRIC_PALLET_JACK: "electricPalletJack",
  CRANE: "crane",
  LOADING_RAMP: "loadingRamp",
  TAIL_LIFT: "tailLift",
  HAND_LOAD: "handLoad",
  CONVEYOR: "conveyor",
  OTHER: "other",
};

const NORMAL_POINT_LIMITS = {
  length: 13.6,
  width: 2.5,
  height: 2.7,
};

const SPECIAL_POINT_LIMITS = {
  length: 20,
  width: 4,
  height: 5,
};

function safeDocumentBody() {
  return typeof document !== "undefined"
    ? document.body
    : null;
}

function getStopKey(stop) {
  return `${stop.__sourceType}-${
    stop.id ??
    `index-${stop.__sourceIndex}`
  }`;
}

function getStopTitle(stop, fallback) {
  return (
    stop?.location ||
    stop?.placeName ||
    fallback
  );
}

function getStopLocation(stop, fallback) {
  const cityCountry = [
    stop?.city,
    stop?.country,
  ]
    .map((value) =>
      String(value || "").trim()
    )
    .filter(Boolean)
    .join(", ");

  return (
    cityCountry ||
    stop?.address ||
    fallback
  );
}

function formatDate(
  value,
  locale,
  fallback
) {
  const rawValue = String(
    value || ""
  ).trim();

  if (!rawValue) {
    return fallback;
  }

  const match = rawValue.match(
    /^(\d{4})-(\d{2})-(\d{2})$/
  );

  if (!match) {
    return rawValue;
  }

  return new Intl.DateTimeFormat(
    locale,
    {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }
  ).format(
    new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3])
    )
  );
}

function getStopDateLabel(
  stop,
  locale,
  undefinedLabel
) {
  const date = formatDate(
    stop?.date,
    locale,
    undefinedLabel
  );

  const hour = String(
    stop?.hour || ""
  ).trim();

  return hour
    ? `${date} ${hour}`
    : date;
}

function normalizeDisplayValue(value) {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ""
  ) {
    return "";
  }

  if (typeof value === "object") {
    if (Array.isArray(value)) {
      return value.join(", ");
    }

    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }

  return String(value);
}

function getDisplayValue(
  value,
  suffix = "",
  undefinedLabel
) {
  const normalized =
    normalizeDisplayValue(value);

  return normalized
    ? `${normalized}${suffix}`
    : undefinedLabel;
}

function parseWorkingHours(value) {
  if (!value) {
    return null;
  }

  if (typeof value === "object") {
    return value;
  }

  if (typeof value === "string") {
    try {
      const parsed =
        JSON.parse(value);

      return (
        parsed &&
        typeof parsed === "object"
      )
        ? parsed
        : null;
    } catch {
      return null;
    }
  }

  return null;
}

function weekdayKeyFromISO(value) {
  if (!value) {
    return null;
  }

  const date =
    new Date(
      `${String(value).slice(0, 10)}T12:00:00`
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return null;
  }

  return (
    DAY_KEYS[
      date.getDay()
    ] ||
    null
  );
}

function formatIntervalsForDay(
  workingHours,
  dayKey,
  closedText
) {
  if (
    !workingHours ||
    !dayKey
  ) {
    return null;
  }

  const intervals =
    workingHours[dayKey];

  if (!Array.isArray(intervals)) {
    return null;
  }

  if (intervals.length === 0) {
    return closedText;
  }

  return intervals
    .map((interval) => {
      if (!Array.isArray(interval)) {
        return null;
      }

      const [
        start,
        end,
      ] = interval;

      if (!start && !end) {
        return null;
      }

      return [
        start,
        end,
      ]
        .filter(Boolean)
        .join("–");
    })
    .filter(Boolean)
    .join(", ");
}

function getTranslatedEnumLabel(
  value,
  keyMap,
  prefix,
  t
) {
  const normalized =
    String(value || "")
      .trim()
      .toUpperCase();

  if (!normalized) {
    return "";
  }

  const key =
    keyMap[normalized];

  return key
    ? t(
        `${prefix}.${key}`
      )
    : normalized;
}

function resolvePlaceSelectValue(
  point,
  options
) {
  if (point?.placeId != null) {
    const byId =
      options.find(
        (option) =>
          String(option.value) ===
          String(point.placeId)
      );

    if (byId) {
      return byId;
    }
  }

  if (point?.location) {
    const byLabel =
      options.find(
        (option) =>
          option.label ===
          point.location
      );

    if (byLabel) {
      return byLabel;
    }

    return {
      value: point.location,
      label: point.location,
      metaCity: point.city || "",
      metaCountry: point.country || "",
      fullData: null,
    };
  }

  return null;
}

function renderPlaceOption(
  option,
  {
    context,
  }
) {
  const city =
    option?.metaCity ||
    option?.fullData?.city ||
    "";

  const country =
    option?.metaCountry ||
    option?.fullData?.country ||
    "";

  const meta = [
    city,
    country,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="current-load-place-option">
      <strong>
        {option.label}
      </strong>

      {meta && (
        <span
          className={
            context === "value"
              ? "is-value"
              : ""
          }
        >
          {meta}
        </span>
      )}
    </div>
  );
}

function pointHasValidationError(point) {
  return Boolean(
    point?.locationError ||
    point?.dateError ||
    point?.hourError ||
    point?.lengthError ||
    point?.widthError ||
    point?.heightError ||
    point?.handlingEquipmentOtherError
  );
}

function DetailItem({
  label,
  value,
  suffix = "",
  undefinedLabel,
  children,
  error,
}) {
  return (
    <div
      className={`edit-request-stop-detail-item ${
        error
          ? "current-load-detail-item-error"
          : ""
      }`}
    >
      <span>
        {label}
      </span>

      {children || (
        <strong>
          {getDisplayValue(
            value,
            suffix,
            undefinedLabel
          )}
        </strong>
      )}

      {error && (
        <small className="current-load-inline-error">
          {error}
        </small>
      )}
    </div>
  );
}

function TextEditor({
  type = "text",
  value,
  onChange,
  step,
  min,
}) {
  return (
    <input
      type={type}
      value={
        value ??
        ""
      }
      step={step}
      min={min}
      onChange={(event) =>
        onChange(
          event.target.value
        )
      }
    />
  );
}

function makeEmptyNewStop(
  type
) {
  return {
    id: null,
    sequence: null,
    type,
    location: "",
    date: "",
    hour: "",
    length: "",
    width: "",
    height: "",
    placeId: null,
    address: "",
    postalCode: "",
    city: "",
    country: "",
    gMapsLink: "",
    coordinates: "",
    latitude: null,
    longitude: null,
    contactPhone: "",
    workingHours: null,
    reference: "",
    loadingAccess: null,
    handlingEquipment: null,
    handlingEquipmentOther: "",
    showMoreFields: false,
    locationError: false,
    dateError: false,
    hourError: false,
    lengthError: "",
    widthError: "",
    heightError: "",
    handlingEquipmentOtherError: false,
  };
}

export default function CurrentLoadRoutePanel({
  pickupPoints = [],
  setPickupPoints,
  deliveryPoints = [],
  setDeliveryPoints,
  maxPoints = 7,
  onRemovePickup,
  onRemoveDelivery,
  placeOptions = [],
  loadingAccessOptions = [],
  handlingEquipmentOptions = [],
  isSpecialLoad = false,
}) {
  const {
    t,
    i18n,
  } =
    useTranslation(
      "edit_load"
    );

  const locale =
    i18n.resolvedLanguage ||
    i18n.language ||
    "pt";

  const [
    expandedStopKey,
    setExpandedStopKey,
  ] =
    useState("");

  const [
    editingStopKey,
    setEditingStopKey,
  ] =
    useState("");

  const [
    draft,
    setDraft,
  ] =
    useState(null);

  const [
    addMenuOpen,
    setAddMenuOpen,
  ] =
    useState(false);

  const [
    newStopType,
    setNewStopType,
  ] =
    useState("");

  const [
    newStopDraft,
    setNewStopDraft,
  ] =
    useState(null);

  const newStopDateInputRef =
    useRef(null);

  const editSnapshotRef =
    useRef(null);

  const routeStops =
    useMemo(
      () => [
        ...pickupPoints.map(
          (
            point,
            index
          ) => ({
            ...point,

            __sourceType:
              "PICKUP",

            __sourceIndex:
              index,

            __stopNumber:
              index + 1,
          })
        ),

        ...deliveryPoints.map(
          (
            point,
            index
          ) => ({
            ...point,

            __sourceType:
              "DELIVERY",

            __sourceIndex:
              index,

            __stopNumber:
              pickupPoints.length +
              index +
              1,
          })
        ),
      ],
      [
        pickupPoints,
        deliveryPoints,
      ]
    );

  useEffect(() => {
    const invalidStop =
      routeStops.find(
        pointHasValidationError
      );

    if (invalidStop) {
      setExpandedStopKey(
        getStopKey(
          invalidStop
        )
      );
    }
  }, [routeStops]);

  useEffect(
    () => {
      if (
        !newStopDraft
      ) {
        return undefined;
      }

      const handleKeyDown =
        (event) => {
          if (
            event.key ===
            "Escape"
          ) {
            setNewStopDraft(
              null
            );

            setNewStopType(
              ""
            );
          }
        };

      document.body.classList.add(
        "current-load-new-stop-modal-open"
      );

      window.addEventListener(
        "keydown",
        handleKeyDown
      );

      return () => {
        document.body.classList.remove(
          "current-load-new-stop-modal-open"
        );

        window.removeEventListener(
          "keydown",
          handleKeyDown
        );
      };
    },
    [
      newStopDraft,
    ]
  );

  const replaceSourceStop =
    (
      stop,
      nextStop
    ) => {
      const sanitizedStop = {
        ...nextStop,
      };

      delete sanitizedStop.__sourceType;
      delete sanitizedStop.__sourceIndex;
      delete sanitizedStop.__stopNumber;

      if (
        stop.__sourceType ===
        "PICKUP"
      ) {
        setPickupPoints(
          (currentPoints) =>
            currentPoints.map(
              (
                point,
                index
              ) =>
                index ===
                stop.__sourceIndex
                  ? {
                      ...point,
                      ...sanitizedStop,
                    }
                  : point
            )
        );

        return;
      }

      setDeliveryPoints(
        (currentPoints) =>
          currentPoints.map(
            (
              point,
              index
            ) =>
              index ===
              stop.__sourceIndex
                ? {
                    ...point,
                    ...sanitizedStop,
                  }
                : point
          )
      );
    };

  const startEditing =
    (stop) => {
      editSnapshotRef.current = {
        stop: {
          ...stop,
        },

        value: {
          ...stop,
        },
      };

      setExpandedStopKey(
        getStopKey(stop)
      );

      setEditingStopKey(
        getStopKey(stop)
      );

      setDraft({
        ...stop,
      });
    };

  const applyDraftPatch =
    (
      stop,
      patch
    ) => {
      const nextDraft = {
        ...(draft || stop),
        ...patch,
      };

      setDraft(
        nextDraft
      );

      replaceSourceStop(
        stop,
        nextDraft
      );
    };

  const cancelEditing =
    () => {
      const snapshot =
        editSnapshotRef.current;

      if (
        snapshot?.stop &&
        snapshot?.value
      ) {
        replaceSourceStop(
          snapshot.stop,
          snapshot.value
        );
      }

      editSnapshotRef.current =
        null;

      setEditingStopKey("");
      setDraft(null);
    };

  const finishEditing =
    () => {
      editSnapshotRef.current =
        null;

      setEditingStopKey("");
      setDraft(null);
    };

  const removeStop =
    (stop) => {
      if (
        stop.__sourceType ===
        "PICKUP"
      ) {
        onRemovePickup?.(
          stop.__sourceIndex
        );
      } else {
        onRemoveDelivery?.(
          stop.__sourceIndex
        );
      }

      setExpandedStopKey("");
      setEditingStopKey("");
      setDraft(null);

      editSnapshotRef.current =
        null;
    };

  const handlePlaceChange =
    (
      stop,
      point,
      selectedOption
    ) => {
      const currentReference =
        point.reference ??
        "";

      if (!selectedOption) {
        applyDraftPatch(
          stop,
          {
            placeId: null,
            location: "",
            address: "",
            postalCode: "",
            city: "",
            country: "",
            gMapsLink: "",
            coordinates: "",
            latitude: null,
            longitude: null,
            contactPhone: "",
            workingHours: null,
            locationError: false,
            reference: currentReference,
          }
        );

        return;
      }

      if (
        selectedOption.__isNew__
      ) {
        applyDraftPatch(
          stop,
          {
            placeId: null,

            location:
              selectedOption.label ||
              selectedOption.value ||
              "",

            address: "",
            postalCode: "",
            city: "",
            country: "",
            gMapsLink: "",
            coordinates: "",
            latitude: null,
            longitude: null,
            contactPhone: "",
            workingHours: null,
            loadingAccess: null,
            handlingEquipment: null,
            handlingEquipmentOther: "",
            handlingEquipmentOtherError: false,
            locationError: false,
            reference: currentReference,
          }
        );

        return;
      }

      const place =
        selectedOption.fullData;

      if (place) {
        applyDraftPatch(
          stop,
          {
            placeId:
              place.id ??
              selectedOption.value ??
              null,

            location:
              place.placeName ||
              place.name ||
              selectedOption.label ||
              "",

            address:
              place.address ||
              "",

            postalCode:
              place.postalCode ||
              "",

            city:
              place.city ||
              "",

            country:
              place.country ||
              "",

            gMapsLink:
              place.gMapsLink ||
              "",

            coordinates:
              place.coordinates ||
              "",

            latitude:
              place.latitude ??
              place.lat ??
              null,

            longitude:
              place.longitude ??
              place.lng ??
              place.lon ??
              null,

            contactPhone:
              place.contactPhone ||
              "",

            workingHours:
              place.workingHours ??
              null,

            locationError:
              false,

            reference:
              currentReference,
          }
        );

        return;
      }

      applyDraftPatch(
        stop,
        {
          placeId:
            selectedOption.value ??
            null,

          location:
            selectedOption.label ||
            "",

          locationError:
            false,

          reference:
            currentReference,
        }
      );
    };

  const closeNewStopModal =
    () => {
      setNewStopDraft(
        null
      );

      setNewStopType(
        ""
      );
    };

  const openNewStopModal =
    (
      type
    ) => {
      if (
        type ===
          "PICKUP" &&
        pickupPoints.length >=
          maxPoints
      ) {
        return;
      }

      if (
        type ===
          "DELIVERY" &&
        deliveryPoints.length >=
          maxPoints
      ) {
        return;
      }

      setAddMenuOpen(
        false
      );

      setNewStopType(
        type
      );

      setNewStopDraft(
        makeEmptyNewStop(
          type
        )
      );
    };

  const patchNewStop =
    (
      patch
    ) => {
      setNewStopDraft(
        (
          currentDraft
        ) =>
          currentDraft
            ? {
                ...currentDraft,
                ...patch,
              }
            : currentDraft
      );
    };

  const handleNewStopPlaceChange =
    (
      selectedOption
    ) => {
      if (
        !selectedOption
      ) {
        patchNewStop({
          placeId: null,
          location: "",
          address: "",
          postalCode: "",
          city: "",
          country: "",
          gMapsLink: "",
          coordinates: "",
          latitude: null,
          longitude: null,
          contactPhone: "",
          workingHours: null,
          locationError: false,
        });

        return;
      }

      if (
        selectedOption.__isNew__
      ) {
        patchNewStop({
          placeId: null,

          location:
            selectedOption.label ||
            selectedOption.value ||
            "",

          address: "",
          postalCode: "",
          city: "",
          country: "",
          gMapsLink: "",
          coordinates: "",
          latitude: null,
          longitude: null,
          contactPhone: "",
          workingHours: null,
          locationError: false,
        });

        return;
      }

      const place =
        selectedOption.fullData;

      if (
        place
      ) {
        patchNewStop({
          placeId:
            place.id ??
            selectedOption.value ??
            null,

          location:
            place.placeName ||
            place.name ||
            selectedOption.label ||
            "",

          address:
            place.address ||
            "",

          postalCode:
            place.postalCode ||
            "",

          city:
            place.city ||
            "",

          country:
            place.country ||
            "",

          gMapsLink:
            place.gMapsLink ||
            "",

          coordinates:
            place.coordinates ||
            "",

          latitude:
            place.latitude ??
            place.lat ??
            null,

          longitude:
            place.longitude ??
            place.lng ??
            place.lon ??
            null,

          contactPhone:
            place.contactPhone ||
            "",

          workingHours:
            place.workingHours ??
            null,

          locationError:
            false,
        });

        return;
      }

      patchNewStop({
        placeId:
          selectedOption.value ??
          null,

        location:
          selectedOption.label ||
          "",

        locationError:
          false,
      });
    };

  const confirmNewStop =
    () => {
      if (
        !newStopDraft ||
        !newStopType
      ) {
        return;
      }

      const dimensionLimits =
        isSpecialLoad
          ? SPECIAL_POINT_LIMITS
          : NORMAL_POINT_LIMITS;

      const getDimensionError =
        (
          field,
          value
        ) => {
          const cleanValue =
            String(
              value ??
              ""
            ).trim();

          if (
            !cleanValue
          ) {
            return "";
          }

          const parsedValue =
            Number(
              cleanValue.replace(
                ",",
                "."
              )
            );

          if (
            !Number.isFinite(
              parsedValue
            ) ||
            parsedValue <=
              0
          ) {
            return t(
              "errors.positiveNumber"
            );
          }

          if (
            parsedValue >
            dimensionLimits[
              field
            ]
          ) {
            if (
              field ===
              "length"
            ) {
              return isSpecialLoad
                ? t(
                    "errors.specialLengthMax"
                  )
                : t(
                    "errors.lengthMax"
                  );
            }

            if (
              field ===
              "width"
            ) {
              return isSpecialLoad
                ? t(
                    "errors.specialWidthMax"
                  )
                : t(
                    "errors.widthMax"
                  );
            }

            return isSpecialLoad
              ? t(
                  "errors.specialHeightMax"
                )
              : t(
                  "errors.heightMax"
                );
          }

          return "";
        };

      const locationError =
        !String(
          newStopDraft.location ||
          ""
        ).trim();

      const dateError =
        !String(
          newStopDraft.date ||
          ""
        ).trim();

      const hourError =
        !/^([01]\d|2[0-3]):([0-5]\d)$/.test(
          String(
            newStopDraft.hour ||
            ""
          ).trim()
        );

      const lengthError =
        getDimensionError(
          "length",
          newStopDraft.length
        );

      const widthError =
        getDimensionError(
          "width",
          newStopDraft.width
        );

      const heightError =
        getDimensionError(
          "height",
          newStopDraft.height
        );

      const handlingEquipmentOtherError =
        String(
          newStopDraft.handlingEquipment ||
          ""
        )
          .trim()
          .toUpperCase() ===
          "OTHER" &&
        !String(
          newStopDraft.handlingEquipmentOther ||
          ""
        ).trim();

      const validatedDraft = {
        ...newStopDraft,

        type:
          newStopType,

        locationError,
        dateError,
        hourError,
        lengthError,
        widthError,
        heightError,
        handlingEquipmentOtherError,
      };

      setNewStopDraft(
        validatedDraft
      );

      if (
        locationError ||
        dateError ||
        hourError ||
        lengthError ||
        widthError ||
        heightError ||
        handlingEquipmentOtherError
      ) {
        return;
      }

      if (
        newStopType ===
        "PICKUP"
      ) {
        if (
          pickupPoints.length >=
          maxPoints
        ) {
          return;
        }

        setPickupPoints(
          (
            currentPoints
          ) => [
            ...currentPoints,
            validatedDraft,
          ]
        );
      } else {
        if (
          deliveryPoints.length >=
          maxPoints
        ) {
          return;
        }

        setDeliveryPoints(
          (
            currentPoints
          ) => [
            ...currentPoints,
            validatedDraft,
          ]
        );
      }

      closeNewStopModal();
    };

  const undefinedLabel =
    t(
      "common.undefined",
      {
        defaultValue:
          "Não definido",
      }
    );

  return (
    <section className="shipment-v2-panel shipment-v2-route-panel pendent-workspace-route-panel current-load-route-panel">
      <div className="shipment-v2-panel-header">
        <h2>
          {t(
            "workspace.routeTitle",
            {
              defaultValue:
                "Rota da carga",
            }
          )}
        </h2>

        <span className="shipment-v2-plain-action current-load-route-count">
          {t(
            "workspace.stopCount",
            {
              defaultValue:
                "{{count}} pontos",

              count:
                routeStops.length,
            }
          )}
        </span>
      </div>

      <div className="shipment-v2-stops-list">
        {routeStops.map((stop) => {
          const stopKey =
            getStopKey(stop);

          const isPickup =
            stop.__sourceType ===
            "PICKUP";

          const isExpanded =
            expandedStopKey ===
            stopKey;

          const isEditing =
            editingStopKey ===
            stopKey;

          const displayStop =
            isEditing &&
            draft
              ? draft
              : stop;

          const stateClass =
            isPickup
              ? "pending"
              : "delivery";

          const dayKey =
            weekdayKeyFromISO(
              displayStop.date
            );

          const workingHoursText =
            formatIntervalsForDay(
              parseWorkingHours(
                displayStop.workingHours
              ),
              dayKey,
              t(
                "workingHours.closed"
              )
            );

          const translatedLoadingAccess =
            getTranslatedEnumLabel(
              displayStop.loadingAccess,
              LOADING_ACCESS_I18N_KEYS,
              "loadingAccess.options",
              t
            );

          const translatedHandlingEquipment =
            getTranslatedEnumLabel(
              displayStop.handlingEquipment,
              HANDLING_EQUIPMENT_I18N_KEYS,
              "handlingEquipment.options",
              t
            );

          const handlingEquipmentDisplay =
            String(
              displayStop.handlingEquipment ||
              ""
            )
              .trim()
              .toUpperCase() ===
              "OTHER" &&
            displayStop.handlingEquipmentOther
              ? `${translatedHandlingEquipment} · ${displayStop.handlingEquipmentOther}`
              : translatedHandlingEquipment;

          return (
            <div
              key={stopKey}
              className="shipment-v2-stop-row"
            >
              <span
                className="shipment-v2-drag-btn pendent-workspace-drag-placeholder"
                aria-hidden="true"
              />

              <article
                className={[
                  "shipment-v2-stop-card",
                  stateClass,
                  "edit-request-stop-card",
                  "current-load-stop-card",
                  isExpanded
                    ? "is-expanded"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div className="edit-request-stop-summary">
                  <div className="shipment-v2-stop-timeline">
                    <span>
                      {stop.__stopNumber}
                    </span>
                  </div>

                  <div className="shipment-v2-stop-content">
                    <div className="shipment-v2-stop-top">
                      <span className={`shipment-v2-stop-type ${stateClass}`}>
                        {isPickup
                          ? t(
                              "fields.pickUpPlace"
                            )
                          : t(
                              "fields.deliveryPlace"
                            )}
                      </span>

                      <span className={`shipment-v2-stop-status ${stateClass}`}>
                        {displayStop.location
                          ? t(
                              "common.configured",
                              {
                                defaultValue:
                                  "Configurado",
                              }
                            )
                          : undefinedLabel}
                      </span>
                    </div>

                    <h3>
                      {getStopTitle(
                        displayStop,
                        undefinedLabel
                      )}
                    </h3>

                    <p>
                      {getStopLocation(
                        displayStop,
                        undefinedLabel
                      )}
                    </p>

                    <div className="shipment-v2-stop-date">
                      <CalendarDays
                        size={14}
                      />

                      {getStopDateLabel(
                        displayStop,
                        locale,
                        undefinedLabel
                      )}
                    </div>
                  </div>

                  <div className="edit-request-stop-summary-actions">
                    <div className="shipment-v2-stop-side-icon">
                      {isPickup ? (
                        <CheckCircle2
                          size={18}
                        />
                      ) : (
                        <Clock3
                          size={18}
                        />
                      )}
                    </div>

                    <button
                      type="button"
                      className={`edit-request-stop-expand-btn ${
                        isExpanded
                          ? "is-open"
                          : ""
                      }`}
                      aria-expanded={isExpanded}
                      onClick={() =>
                        setExpandedStopKey(
                          (currentKey) =>
                            currentKey ===
                            stopKey
                              ? ""
                              : stopKey
                        )
                      }
                    >
                      <ChevronDown
                        size={19}
                      />
                    </button>
                  </div>
                </div>

                <div
                  className={`edit-request-stop-details-wrapper ${
                    isExpanded
                      ? "is-open"
                      : ""
                  }`}
                >
                  <div className="edit-request-stop-details-inner">
                    <div className="edit-request-stop-details-panel">
                      <div className="edit-request-stop-details-grid current-load-stop-details-grid">
                        <DetailItem
                          label={
                            isPickup
                              ? t(
                                  "fields.pickUpPlace"
                                )
                              : t(
                                  "fields.deliveryPlace"
                                )
                          }
                          value={displayStop.location}
                          undefinedLabel={undefinedLabel}
                          error={
                            displayStop.locationError
                              ? t(
                                  "errors.locationRequired"
                                )
                              : ""
                          }
                        >
                          {isEditing ? (
                            <CreatableSelect
                              isClearable
                              isMulti={false}
                              options={placeOptions}
                              placeholder={t(
                                "place.selectOrType"
                              )}
                              value={resolvePlaceSelectValue(
                                displayStop,
                                placeOptions
                              )}
                              formatOptionLabel={
                                renderPlaceOption
                              }
                              formatCreateLabel={(value) =>
                                t(
                                  "place.createOption",
                                  {
                                    value,
                                  }
                                )
                              }
                              onChange={(option) =>
                                handlePlaceChange(
                                  stop,
                                  displayStop,
                                  option
                                )
                              }
                              noOptionsMessage={() =>
                                t(
                                  "common.noOptions"
                                )
                              }
                              classNamePrefix="current-load-workspace-select"
                              menuPortalTarget={safeDocumentBody()}
                              menuPosition="fixed"
                            />
                          ) : null}
                        </DetailItem>

                        <DetailItem
                          label={t(
                            "routePanel.fields.date",
                            {
                              defaultValue:
                                "Data",
                            }
                          )}
                          value={displayStop.date}
                          undefinedLabel={undefinedLabel}
                          error={
                            displayStop.dateError
                              ? t(
                                  "errors.dateRequired"
                                )
                              : ""
                          }
                        >
                          {isEditing ? (
                            <TextEditor
                              type="date"
                              value={displayStop.date}
                              onChange={(value) =>
                                applyDraftPatch(
                                  stop,
                                  {
                                    date: value,
                                    dateError:
                                      false,
                                  }
                                )
                              }
                            />
                          ) : null}
                        </DetailItem>

                        <DetailItem
                          label={t(
                            "routePanel.fields.hour",
                            {
                              defaultValue:
                                "Hora",
                            }
                          )}
                          value={displayStop.hour}
                          undefinedLabel={undefinedLabel}
                          error={
                            displayStop.hourError
                              ? t(
                                  "errors.hourInvalid"
                                )
                              : ""
                          }
                        >
                          {isEditing ? (
                            <TextEditor
                              type="time"
                              value={displayStop.hour}
                              onChange={(value) =>
                                applyDraftPatch(
                                  stop,
                                  {
                                    hour: value,
                                    hourError:
                                      false,
                                  }
                                )
                              }
                            />
                          ) : null}
                        </DetailItem>

                        <DetailItem
                          label={t(
                            "fields.pointReference"
                          )}
                          value={displayStop.reference}
                          undefinedLabel={undefinedLabel}
                        >
                          {isEditing ? (
                            <TextEditor
                              value={displayStop.reference}
                              onChange={(value) =>
                                applyDraftPatch(
                                  stop,
                                  {
                                    reference:
                                      value,
                                  }
                                )
                              }
                            />
                          ) : null}
                        </DetailItem>

                        <DetailItem
                          label={t(
                            "placeholders.address"
                          )}
                          value={displayStop.address}
                          undefinedLabel={undefinedLabel}
                        >
                          {isEditing ? (
                            <TextEditor
                              value={displayStop.address}
                              onChange={(value) =>
                                applyDraftPatch(
                                  stop,
                                  {
                                    address:
                                      value,
                                  }
                                )
                              }
                            />
                          ) : null}
                        </DetailItem>

                        <DetailItem
                          label={t(
                            "placeholders.postalCode"
                          )}
                          value={displayStop.postalCode}
                          undefinedLabel={undefinedLabel}
                        >
                          {isEditing ? (
                            <TextEditor
                              value={displayStop.postalCode}
                              onChange={(value) =>
                                applyDraftPatch(
                                  stop,
                                  {
                                    postalCode:
                                      value,
                                  }
                                )
                              }
                            />
                          ) : null}
                        </DetailItem>

                        <DetailItem
                          label={t(
                            "placeholders.city"
                          )}
                          value={displayStop.city}
                          undefinedLabel={undefinedLabel}
                        >
                          {isEditing ? (
                            <TextEditor
                              value={displayStop.city}
                              onChange={(value) =>
                                applyDraftPatch(
                                  stop,
                                  {
                                    city:
                                      value,
                                  }
                                )
                              }
                            />
                          ) : null}
                        </DetailItem>

                        <DetailItem
                          label={t(
                            "placeholders.country"
                          )}
                          value={displayStop.country}
                          undefinedLabel={undefinedLabel}
                        >
                          {isEditing ? (
                            <TextEditor
                              value={displayStop.country}
                              onChange={(value) =>
                                applyDraftPatch(
                                  stop,
                                  {
                                    country:
                                      value,
                                  }
                                )
                              }
                            />
                          ) : null}
                        </DetailItem>

                        <DetailItem
                          label={t(
                            "placeholders.contactPhone"
                          )}
                          value={displayStop.contactPhone}
                          undefinedLabel={undefinedLabel}
                        >
                          {isEditing ? (
                            <TextEditor
                              type="tel"
                              value={displayStop.contactPhone}
                              onChange={(value) =>
                                applyDraftPatch(
                                  stop,
                                  {
                                    contactPhone:
                                      value,
                                  }
                                )
                              }
                            />
                          ) : null}
                        </DetailItem>

                        <DetailItem
                          label={t(
                            "fields.pointLengthShort"
                          )}
                          value={displayStop.length}
                          suffix=" m"
                          undefinedLabel={undefinedLabel}
                          error={
                            displayStop.lengthError ||
                            ""
                          }
                        >
                          {isEditing ? (
                            <TextEditor
                              type="number"
                              step="0.01"
                              min="0"
                              value={displayStop.length}
                              onChange={(value) =>
                                applyDraftPatch(
                                  stop,
                                  {
                                    length:
                                      value,

                                    lengthError:
                                      "",
                                  }
                                )
                              }
                            />
                          ) : null}
                        </DetailItem>

                        <DetailItem
                          label={t(
                            "fields.pointWidthShort"
                          )}
                          value={displayStop.width}
                          suffix=" m"
                          undefinedLabel={undefinedLabel}
                          error={
                            displayStop.widthError ||
                            ""
                          }
                        >
                          {isEditing ? (
                            <TextEditor
                              type="number"
                              step="0.01"
                              min="0"
                              value={displayStop.width}
                              onChange={(value) =>
                                applyDraftPatch(
                                  stop,
                                  {
                                    width:
                                      value,

                                    widthError:
                                      "",
                                  }
                                )
                              }
                            />
                          ) : null}
                        </DetailItem>

                        <DetailItem
                          label={t(
                            "fields.pointHeightShort"
                          )}
                          value={displayStop.height}
                          suffix=" m"
                          undefinedLabel={undefinedLabel}
                          error={
                            displayStop.heightError ||
                            ""
                          }
                        >
                          {isEditing ? (
                            <TextEditor
                              type="number"
                              step="0.01"
                              min="0"
                              value={displayStop.height}
                              onChange={(value) =>
                                applyDraftPatch(
                                  stop,
                                  {
                                    height:
                                      value,

                                    heightError:
                                      "",
                                  }
                                )
                              }
                            />
                          ) : null}
                        </DetailItem>

                        <DetailItem
                          label={t(
                            "loadingAccess.label"
                          )}
                          value={
                            translatedLoadingAccess
                          }
                          undefinedLabel={undefinedLabel}
                        >
                          {isEditing ? (
                            <Select
                              isClearable
                              options={loadingAccessOptions}
                              value={
                                loadingAccessOptions.find(
                                  (option) =>
                                    option.value ===
                                    displayStop.loadingAccess
                                ) ||
                                null
                              }
                              onChange={(option) =>
                                applyDraftPatch(
                                  stop,
                                  {
                                    loadingAccess:
                                      option?.value ||
                                      null,
                                  }
                                )
                              }
                              placeholder={t(
                                "loadingAccess.placeholder"
                              )}
                              noOptionsMessage={() =>
                                t(
                                  "common.noOptions"
                                )
                              }
                              classNamePrefix="current-load-workspace-select"
                              menuPortalTarget={safeDocumentBody()}
                              menuPosition="fixed"
                            />
                          ) : null}
                        </DetailItem>

                        <DetailItem
                          label={t(
                            "handlingEquipment.label"
                          )}
                          value={
                            handlingEquipmentDisplay
                          }
                          undefinedLabel={undefinedLabel}
                        >
                          {isEditing ? (
                            <Select
                              isClearable
                              options={
                                handlingEquipmentOptions
                              }
                              value={
                                handlingEquipmentOptions.find(
                                  (option) =>
                                    option.value ===
                                    displayStop.handlingEquipment
                                ) ||
                                null
                              }
                              onChange={(option) =>
                                applyDraftPatch(
                                  stop,
                                  {
                                    handlingEquipment:
                                      option?.value ||
                                      null,

                                    handlingEquipmentOther:
                                      option?.value ===
                                      "OTHER"
                                        ? displayStop.handlingEquipmentOther ||
                                          ""
                                        : "",

                                    handlingEquipmentOtherError:
                                      false,
                                  }
                                )
                              }
                              placeholder={t(
                                "handlingEquipment.placeholder"
                              )}
                              noOptionsMessage={() =>
                                t(
                                  "common.noOptions"
                                )
                              }
                              classNamePrefix="current-load-workspace-select"
                              menuPortalTarget={safeDocumentBody()}
                              menuPosition="fixed"
                            />
                          ) : null}
                        </DetailItem>

                        {isEditing &&
                          String(
                            displayStop.handlingEquipment ||
                            ""
                          )
                            .trim()
                            .toUpperCase() ===
                            "OTHER" && (
                            <DetailItem
                              label={t(
                                "handlingEquipment.otherLabel"
                              )}
                              value={
                                displayStop.handlingEquipmentOther
                              }
                              undefinedLabel={undefinedLabel}
                              error={
                                displayStop.handlingEquipmentOtherError
                                  ? t(
                                      "errors.handlingEquipmentOtherRequired"
                                    )
                                  : ""
                              }
                            >
                              <TextEditor
                                value={
                                  displayStop.handlingEquipmentOther
                                }
                                onChange={(value) =>
                                  applyDraftPatch(
                                    stop,
                                    {
                                      handlingEquipmentOther:
                                        value,

                                      handlingEquipmentOtherError:
                                        false,
                                    }
                                  )
                                }
                              />
                            </DetailItem>
                          )}

                        <DetailItem
                          label={t(
                            "workingHours.label",
                            {
                              defaultValue:
                                "Horário",
                            }
                          )}
                          value={
                            workingHoursText ||
                            t(
                              "workingHours.noInfo"
                            )
                          }
                          undefinedLabel={undefinedLabel}
                        />

                        <DetailItem
                          label={t(
                            "placeholders.gMapsLink"
                          )}
                          value={displayStop.gMapsLink}
                          undefinedLabel={undefinedLabel}
                        >
                          {isEditing ? (
                            <TextEditor
                              value={displayStop.gMapsLink}
                              onChange={(value) =>
                                applyDraftPatch(
                                  stop,
                                  {
                                    gMapsLink:
                                      value,
                                  }
                                )
                              }
                            />
                          ) : null}
                        </DetailItem>

                        <DetailItem
                          label={t(
                            "placeholders.coordinates"
                          )}
                          value={displayStop.coordinates}
                          undefinedLabel={undefinedLabel}
                        >
                          {isEditing ? (
                            <TextEditor
                              value={displayStop.coordinates}
                              onChange={(value) =>
                                applyDraftPatch(
                                  stop,
                                  {
                                    coordinates:
                                      value,
                                  }
                                )
                              }
                            />
                          ) : null}
                        </DetailItem>
                      </div>

                      <div className="edit-request-stop-footer">
                        {isEditing ? (
                          <>
                            <button
                              type="button"
                              className="edit-request-stop-cancel-btn"
                              onClick={cancelEditing}
                            >
                              {t(
                                "buttons.cancel"
                              )}
                            </button>

                            <button
                              type="button"
                              className="edit-request-stop-save-btn"
                              onClick={finishEditing}
                            >
                              <CheckCircle2
                                size={15}
                              />

                              {t(
                                "workspace.finishEditing",
                                {
                                  defaultValue:
                                    "Concluir edição",
                                }
                              )}
                            </button>
                          </>
                        ) : (
                          <div className="pendent-workspace-stop-footer-actions">
                            <button
                              type="button"
                              className="pendent-workspace-remove-stop-btn"
                              disabled={
                                isPickup
                                  ? pickupPoints.length <=
                                    1
                                  : deliveryPoints.length <=
                                    1
                              }
                              onClick={() =>
                                removeStop(stop)
                              }
                            >
                              <X size={15} />

                              {t(
                                "workspace.removeStop",
                                {
                                  defaultValue:
                                    "Remover",
                                }
                              )}
                            </button>

                            <button
                              type="button"
                              className="edit-request-stop-edit-btn"
                              onClick={() =>
                                startEditing(
                                  stop
                                )
                              }
                            >
                              <Pencil
                                size={15}
                              />

                              {t(
                                "workspace.editDetails",
                                {
                                  defaultValue:
                                    "Editar detalhes",
                                }
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          );
        })}
      </div>

      <div className="pendent-workspace-add-stop-wrap">
        <button
          type="button"
          className="shipment-v2-add-stop-btn"
          onClick={() =>
            setAddMenuOpen(
              (currentValue) =>
                !currentValue
            )
          }
        >
          {t(
            "workspace.addStop",
            {
              defaultValue:
                "Adicionar ponto",
            }
          )}
        </button>

        {addMenuOpen && (
          <div className="pendent-workspace-add-stop-menu">
            <button
              type="button"
              disabled={
                pickupPoints.length >=
                maxPoints
              }
              onClick={() =>
                openNewStopModal(
                  "PICKUP"
                )
              }
            >
              {t(
                "buttons.addPickup"
              )}
            </button>

            <button
              type="button"
              disabled={
                deliveryPoints.length >=
                maxPoints
              }
              onClick={() =>
                openNewStopModal(
                  "DELIVERY"
                )
              }
            >
              {t(
                "buttons.addDelivery"
              )}
            </button>
          </div>
        )}
      </div>

      <div className="shipment-v2-route-note">
        <Info size={15} />

        {t(
          "workspace.routeEditNote",
          {
            defaultValue:
              "Expande um ponto para consultar os detalhes. Usa ‘Editar detalhes’ para alterar os campos existentes.",
          }
        )}
      </div>

      {newStopDraft && (
        <div
          className="current-load-new-stop-modal-overlay"
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              closeNewStopModal();
            }
          }}
        >
          <div
            className="current-load-new-stop-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="current-load-new-stop-modal-title"
          >
            <div className="current-load-new-stop-modal__header">
              <div>
                <span className={`current-load-new-stop-modal__type ${
                  newStopType ===
                  "PICKUP"
                    ? "is-pickup"
                    : "is-delivery"
                }`}>
                  {newStopType ===
                  "PICKUP"
                    ? t(
                        "fields.pickUpPlace"
                      )
                    : t(
                        "fields.deliveryPlace"
                      )}
                </span>

                <h2 id="current-load-new-stop-modal-title">
                  {newStopType ===
                  "PICKUP"
                    ? t(
                        "workspace.newPickupTitle"
                      )
                    : t(
                        "workspace.newDeliveryTitle"
                      )}
                </h2>

                <p>
                  {t(
                    "workspace.newStopHelper"
                  )}
                </p>
              </div>

              <button
                type="button"
                className="current-load-new-stop-modal__close"
                aria-label={t(
                  "buttons.cancel"
                )}
                onClick={
                  closeNewStopModal
                }
              >
                <X size={18} />
              </button>
            </div>

            <div className="current-load-new-stop-modal__body">
              <div className="current-load-new-stop-modal__grid">
                <label className="current-load-new-stop-modal__field current-load-new-stop-modal__field--wide">
                  <span>
                    {newStopType ===
                    "PICKUP"
                      ? t(
                          "fields.pickUpPlace"
                        )
                      : t(
                          "fields.deliveryPlace"
                        )}
                  </span>

                  <CreatableSelect
                    isClearable
                    isMulti={false}
                    options={
                      placeOptions
                    }
                    placeholder={t(
                      "place.selectOrType"
                    )}
                    value={resolvePlaceSelectValue(
                      newStopDraft,
                      placeOptions
                    )}
                    formatOptionLabel={
                      renderPlaceOption
                    }
                    formatCreateLabel={(value) =>
                      t(
                        "place.createOption",
                        {
                          value,
                        }
                      )
                    }
                    onChange={
                      handleNewStopPlaceChange
                    }
                    noOptionsMessage={() =>
                      t(
                        "common.noOptions"
                      )
                    }
                    classNamePrefix="current-load-workspace-select"
                    menuPortalTarget={safeDocumentBody()}
                    menuPosition="fixed"
                  />

                  {newStopDraft.locationError && (
                    <small className="current-load-new-stop-modal__error">
                      {t(
                        "errors.locationRequired"
                      )}
                    </small>
                  )}
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "routePanel.fields.date"
                    )}
                  </span>

                  <div className="current-load-new-stop-modal__input-with-icon">
                    <input
                      ref={
                        newStopDateInputRef
                      }
                      type="date"
                      value={
                        newStopDraft.date ||
                        ""
                      }
                      onChange={(event) =>
                        patchNewStop({
                          date:
                            event.target.value,

                          dateError:
                            false,
                        })
                      }
                    />

                    <button
                      type="button"
                      className="current-load-new-stop-modal__field-icon-btn"
                      aria-label={t(
                        "routePanel.fields.date"
                      )}
                      onClick={() => {
                        if (
                          newStopDateInputRef.current
                            ?.showPicker
                        ) {
                          newStopDateInputRef.current.showPicker();
                          return;
                        }

                        newStopDateInputRef.current?.focus();
                      }}
                    >
                      <CalendarDays
                        size={16}
                      />
                    </button>
                  </div>

                  {newStopDraft.dateError && (
                    <small className="current-load-new-stop-modal__error">
                      {t(
                        "errors.dateRequired"
                      )}
                    </small>
                  )}
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "routePanel.fields.hour"
                    )}
                  </span>

                  <input
                    type="time"
                    value={
                      newStopDraft.hour ||
                      ""
                    }
                    onChange={(event) =>
                      patchNewStop({
                        hour:
                          event.target.value,

                        hourError:
                          false,
                      })
                    }
                  />

                  {newStopDraft.hourError && (
                    <small className="current-load-new-stop-modal__error">
                      {t(
                        "errors.hourInvalid"
                      )}
                    </small>
                  )}
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "fields.pointReference"
                    )}
                  </span>

                  <input
                    type="text"
                    value={
                      newStopDraft.reference ||
                      ""
                    }
                    onChange={(event) =>
                      patchNewStop({
                        reference:
                          event.target.value,
                      })
                    }
                  />
                </label>

                <label className="current-load-new-stop-modal__field current-load-new-stop-modal__field--wide">
                  <span>
                    {t(
                      "placeholders.address"
                    )}
                  </span>

                  <input
                    type="text"
                    value={
                      newStopDraft.address ||
                      ""
                    }
                    onChange={(event) =>
                      patchNewStop({
                        address:
                          event.target.value,
                      })
                    }
                  />
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "placeholders.postalCode"
                    )}
                  </span>

                  <input
                    type="text"
                    value={
                      newStopDraft.postalCode ||
                      ""
                    }
                    onChange={(event) =>
                      patchNewStop({
                        postalCode:
                          event.target.value,
                      })
                    }
                  />
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "placeholders.city"
                    )}
                  </span>

                  <input
                    type="text"
                    value={
                      newStopDraft.city ||
                      ""
                    }
                    onChange={(event) =>
                      patchNewStop({
                        city:
                          event.target.value,
                      })
                    }
                  />
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "placeholders.country"
                    )}
                  </span>

                  <input
                    type="text"
                    value={
                      newStopDraft.country ||
                      ""
                    }
                    onChange={(event) =>
                      patchNewStop({
                        country:
                          event.target.value,
                      })
                    }
                  />
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "placeholders.contactPhone"
                    )}
                  </span>

                  <input
                    type="tel"
                    value={
                      newStopDraft.contactPhone ||
                      ""
                    }
                    onChange={(event) =>
                      patchNewStop({
                        contactPhone:
                          event.target.value,
                      })
                    }
                  />
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "fields.pointLengthShort"
                    )}
                  </span>

                  <div className="current-load-new-stop-modal__dimension">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={
                        newStopDraft.length ??
                        ""
                      }
                      onChange={(event) =>
                        patchNewStop({
                          length:
                            event.target.value,

                          lengthError:
                            "",
                        })
                      }
                    />

                    <small>
                      {t(
                        "units.meterShort"
                      )}
                    </small>
                  </div>

                  {newStopDraft.lengthError && (
                    <small className="current-load-new-stop-modal__error">
                      {newStopDraft.lengthError}
                    </small>
                  )}
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "fields.pointWidthShort"
                    )}
                  </span>

                  <div className="current-load-new-stop-modal__dimension">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={
                        newStopDraft.width ??
                        ""
                      }
                      onChange={(event) =>
                        patchNewStop({
                          width:
                            event.target.value,

                          widthError:
                            "",
                        })
                      }
                    />

                    <small>
                      {t(
                        "units.meterShort"
                      )}
                    </small>
                  </div>

                  {newStopDraft.widthError && (
                    <small className="current-load-new-stop-modal__error">
                      {newStopDraft.widthError}
                    </small>
                  )}
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "fields.pointHeightShort"
                    )}
                  </span>

                  <div className="current-load-new-stop-modal__dimension">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={
                        newStopDraft.height ??
                        ""
                      }
                      onChange={(event) =>
                        patchNewStop({
                          height:
                            event.target.value,

                          heightError:
                            "",
                        })
                      }
                    />

                    <small>
                      {t(
                        "units.meterShort"
                      )}
                    </small>
                  </div>

                  {newStopDraft.heightError && (
                    <small className="current-load-new-stop-modal__error">
                      {newStopDraft.heightError}
                    </small>
                  )}
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "loadingAccess.label"
                    )}
                  </span>

                  <Select
                    isClearable
                    options={
                      loadingAccessOptions
                    }
                    value={
                      loadingAccessOptions.find(
                        (option) =>
                          option.value ===
                          newStopDraft.loadingAccess
                      ) ||
                      null
                    }
                    onChange={(option) =>
                      patchNewStop({
                        loadingAccess:
                          option?.value ||
                          null,
                      })
                    }
                    placeholder={t(
                      "loadingAccess.placeholder"
                    )}
                    noOptionsMessage={() =>
                      t(
                        "common.noOptions"
                      )
                    }
                    classNamePrefix="current-load-workspace-select"
                    menuPortalTarget={safeDocumentBody()}
                    menuPosition="fixed"
                  />
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "handlingEquipment.label"
                    )}
                  </span>

                  <Select
                    isClearable
                    options={
                      handlingEquipmentOptions
                    }
                    value={
                      handlingEquipmentOptions.find(
                        (option) =>
                          option.value ===
                          newStopDraft.handlingEquipment
                      ) ||
                      null
                    }
                    onChange={(option) =>
                      patchNewStop({
                        handlingEquipment:
                          option?.value ||
                          null,

                        handlingEquipmentOther:
                          option?.value ===
                          "OTHER"
                            ? newStopDraft.handlingEquipmentOther ||
                              ""
                            : "",

                        handlingEquipmentOtherError:
                          false,
                      })
                    }
                    placeholder={t(
                      "handlingEquipment.placeholder"
                    )}
                    noOptionsMessage={() =>
                      t(
                        "common.noOptions"
                      )
                    }
                    classNamePrefix="current-load-workspace-select"
                    menuPortalTarget={safeDocumentBody()}
                    menuPosition="fixed"
                  />
                </label>

                {String(
                  newStopDraft.handlingEquipment ||
                  ""
                )
                  .trim()
                  .toUpperCase() ===
                  "OTHER" && (
                  <label className="current-load-new-stop-modal__field">
                    <span>
                      {t(
                        "handlingEquipment.otherLabel"
                      )}
                    </span>

                    <input
                      type="text"
                      value={
                        newStopDraft.handlingEquipmentOther ||
                        ""
                      }
                      onChange={(event) =>
                        patchNewStop({
                          handlingEquipmentOther:
                            event.target.value,

                          handlingEquipmentOtherError:
                            false,
                        })
                      }
                    />

                    {newStopDraft.handlingEquipmentOtherError && (
                      <small className="current-load-new-stop-modal__error">
                        {t(
                          "errors.handlingEquipmentOtherRequired"
                        )}
                      </small>
                    )}
                  </label>
                )}

                <div className="current-load-new-stop-modal__pair-row">
                  <label className="current-load-new-stop-modal__field">
                    <span>
                      {t(
                        "placeholders.gMapsLink"
                      )}
                    </span>

                    <input
                      type="text"
                      value={
                        newStopDraft.gMapsLink ||
                        ""
                      }
                      onChange={(event) =>
                        patchNewStop({
                          gMapsLink:
                            event.target.value,
                        })
                      }
                    />
                  </label>

                  <label className="current-load-new-stop-modal__field">
                    <span>
                      {t(
                        "placeholders.coordinates"
                      )}
                    </span>

                    <input
                      type="text"
                      value={
                        newStopDraft.coordinates ||
                        ""
                      }
                      onChange={(event) =>
                        patchNewStop({
                          coordinates:
                            event.target.value,
                        })
                      }
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="current-load-new-stop-modal__footer">
              <button
                type="button"
                className="current-load-new-stop-modal__cancel"
                onClick={
                  closeNewStopModal
                }
              >
                {t(
                  "buttons.cancel"
                )}
              </button>

              <button
                type="button"
                className="current-load-new-stop-modal__confirm"
                onClick={
                  confirmNewStop
                }
              >

                {newStopType ===
                "PICKUP"
                  ? t(
                      "buttons.addPickup"
                    )
                  : t(
                      "buttons.addDelivery"
                    )}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}